import type { FC, SetStateAction } from "react";
import { useMemo, useState } from "react";

import { usePatientDocuments } from "@/api/documents";
import DialogCustom from "@/components/modals/dialog-custom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import type { Consultation } from "@/types/api/consultation";
import type { Patient } from "@/types/api/patient";
import { handleGenerateAndMerge } from "@/utils/mergeExport";

interface ExportModalProps {
  patient: Patient;
  consultations: Array<Consultation>;
  open: boolean;
  onOpenChange: React.Dispatch<SetStateAction<boolean>>;
}

const getExtension = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname; // safer than split
    const parts = pathname.split(".");
    if (parts.length < 2) return null;
    return parts.pop()?.toLowerCase() ?? null;
  } catch {
    return null;
  }
};

const isSupportedFile = (url: string, isPdf: boolean): boolean => {
  const ext = getExtension(url);

  if (!ext) return false;

  if (isPdf) {
    return ext === "pdf";
  }

  return ["jpg", "jpeg", "png", "webp"].includes(ext);
};

const ExportModal: FC<ExportModalProps> = ({
  patient,
  consultations,
  open,
  onOpenChange,
}) => {
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data, isLoading } = usePatientDocuments(patient.id.toString(), false);
  const signedDocuments = useMemo(() => {
    if (isLoading || !data) return [];

    return data
      .filter((document) =>
        isSupportedFile(document.s3SignedUrl, document.isPdf)
      )
      .map((signedDocument) => ({
        id: signedDocument.id,
        title: signedDocument.title,
        isPdf: signedDocument.isPdf,
        date: new Date(signedDocument.createdAt),
        src: signedDocument.s3SignedUrl,
      }));
  }, [data, isLoading]);

  const onSubmit = async () => {
    setIsGenerating(true);
    try {
      await handleGenerateAndMerge(
        patient,
        consultations,
        signedDocuments.filter((document) => selectedIds.includes(document.id))
      );
      onOpenChange(false);
    } catch (err) {
      console.error("Error while generating PDF:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <DialogCustom
      open={open}
      onOpenChange={onOpenChange}
      title="Compte-rendu"
      description={
        <div className="text-sm text-gray-600">
          {"Sélectionner les documents à inclure"}
        </div>
      }
    >
      <Separator className="col-span-full bg-gray-200" />
      {isLoading ? (
        <p>Chargement des documents...</p>
      ) : (
        <div className="flex flex-col gap-4 w-full px-6">
          {signedDocuments.map((doc) => (
            <div key={doc.id} className="flex items-center gap-2">
              <Checkbox
                checked={selectedIds.includes(doc.id)}
                onCheckedChange={(checked) => {
                  setSelectedIds((prev) =>
                    checked
                      ? [...prev, doc.id]
                      : prev.filter((id) => id !== doc.id)
                  );
                }}
              />
              <div>
                {`${doc.title} — ${doc.isPdf ? "PDF" : "Image"} — ${doc.date.toLocaleDateString()}`}
              </div>
            </div>
          ))}
        </div>
      )}
      <Separator className="col-span-full bg-gray-200" />
      <DialogFooter className="flex flex-row gap-3 px-6">
        <Button
          type="button"
          variant={"destructive"}
          onClick={() => {
            onOpenChange(false);
          }}
          className="flex-1"
        >
          Annuler
        </Button>
        <Button
          type="button"
          variant={"default"}
          onClick={onSubmit}
          className="flex-1"
          loading={isGenerating}
        >
          Confirmer
        </Button>
      </DialogFooter>
    </DialogCustom>
  );
};

export default ExportModal;
